/**
 * Turn a scoring explanation (usually one long paragraph) into readable
 * bullets for the product-page score card and the "Why this scored" tab.
 *
 * Also opens the product-specific scoring-rubric dialog (How we scored this)
 * and resolves per-dimension WHY text from published metafields / labeled
 * explanation sections / product-specific FAQ — never from generic rubric
 * copy and never invented studies. Each dimension explains what earned
 * the score and what was missing.
 */
(function (root) {
  const SECTION_SPLIT =
    /(?:\s+|(?<=[.!)]))(?=(?:Formulation:|Design:|Results:|Protocol:|Instrumentation:|Held below\b|Would move\b|Holds (?:Verified|Elite|Standard|Rejected|Provisional)\b|Study conducted\b|Primary endpoint\b|Complementary self-report\b|Both papers document\b))/i;

  const MISSING_RATIONALE = 'Rationale not published.';

  const DIMENSION_ORDER = [
    'evidence',
    'formulation',
    'manufacturing',
    'safety',
    'transparency',
    'sustainability',
  ];

  const DIMENSION_LABEL = {
    evidence: 'Evidence',
    formulation: 'Formulation',
    manufacturing: 'Manufacturing',
    safety: 'Safety',
    transparency: 'Transparency',
    sustainability: 'Sustainability',
  };

  const GENERIC_RATIONALE = [
    /dose adequacy, bioavailability form, and ingredient redundancy/i,
    /quality of published RCT support for the active ingredients at the label dose/i,
    /third-party testing, GMP compliance, and facility audit status/i,
    /adverse-event profile, interaction risk, and dose ceiling/i,
    /dose disclosure vs proprietary blends/i,
    /sourcing practices, packaging, and corporate practice/i,
    /double-blind RCTs in MEDLINE-indexed journals/i,
    /exact ingredients at the exact dose/i,
    /No third-party certifications \(NSF, USP, ConsumerLab/i,
    /It is scored on the Vyvata Scoring Framework/i,
    /\/100 overall —/i,
    /The Vyvata Scoring Framework rates every listed product/i,
    /This product triggered the Vyvata auto-fail rule/i,
    /The top-ranked .+ product in the Vyvata catalog/i,
    /This is currently the highest-scoring product/i,
    /The Vyvata score measures product integrity/i,
    /All six dimensions feed the overall Vyvata score/i,
    /Product-specific evidence citations for this item are in How we scored this/i,
  ];

  const FAQ_DIMENSION = [
    ['evidence', /\b(evidence|rct|clinical evidence|studies|pubmed|published trial)\b/i],
    ['formulation', /\b(formula|formulation|ingredient|what'?s actually in)\b/i],
    ['manufacturing', /\b(manufactur|gmp|\bcoa\b|third-party test|facility cert)\b/i],
    ['safety', /\b(safety|adverse|side effect|interact)\b/i],
    ['transparency', /\b(transparenc|disclos|proprietary blend)\b/i],
    ['sustainability', /\b(sustainab|packaging|ethically sourced)\b/i],
  ];

  const LABEL_TO_DIM = [
    ['evidence', /^(?:Evidence(?:\s+quality)?)[:—–-]\s*/i],
    ['formulation', /^(?:Formulation|Design)[:—–-]\s*/i],
    ['manufacturing', /^(?:Manufacturing|Build)[:—–-]\s*/i],
    ['safety', /^(?:Safety(?:\s+profile)?)[:—–-]\s*/i],
    ['transparency', /^(?:Transparency|Brand transparency)[:—–-]\s*/i],
    ['sustainability', /^(?:Sustainability|Ethics)[:—–-]\s*/i],
  ];

  const SHARED_MFG_TR_SU =
    /^(Manufacturing,\s*transparency,\s*and\s*sustainability\b[\s\S]*)/i;

  const FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  function clean(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  function splitLettered(text) {
    const src = clean(text);
    const match = src.match(/\(\s*[a-d]\s*\)\s+/i);
    if (!match || match.index == null) return [src];

    const prefix = src.slice(0, match.index).trim().replace(/[:;]\s*$/, '').replace(/\s+by$/i, '');
    const rest = src.slice(match.index);
    const parts = rest.split(/\s*(?=\(\s*[a-d]\s*\))/i).filter(Boolean);
    const items = [];
    if (prefix) items.push(prefix + ':');
    for (const part of parts) {
      const body = part.replace(/^\(\s*[a-d]\s*\)\s*/i, '').replace(/[.;]\s*$/, '').trim();
      if (!body) continue;
      items.push(body.charAt(0).toUpperCase() + body.slice(1));
    }
    return items;
  }

  function protect(s) {
    return s
      .replace(/\b(St|Dr|Mr|Mrs|Ms|vs|Fig|Vol|pp|Inc|Ltd|Jr|Sr|No|Prof)\./g, function (m) {
        return m.replace('.', '∯');
      })
      .replace(/\b(e\.g|i\.e|U\.S|U\.K|et al)\./gi, function (m) {
        return m.replace(/\./g, '∯');
      })
      .replace(/(\d)\.(\d)/g, '$1∯$2');
  }

  function unprotect(s) {
    return s.replace(/∯/g, '.');
  }

  function splitSentences(text) {
    const protectedText = protect(clean(text));
    const chunks = protectedText.split(/(?<=\.)\s+(?=[A-Z"'“‘])/);
    const merged = [];
    for (const chunk of chunks) {
      const t = unprotect(chunk).trim();
      if (!t) continue;
      if (merged.length && /^['"“‘]/.test(t)) {
        merged[merged.length - 1] += ' ' + t;
        continue;
      }
      merged.push(t);
    }
    return merged.length ? merged : [clean(text)].filter(Boolean);
  }

  function toBullets(text) {
    const raw = clean(text);
    if (!raw) return [];

    const sections = raw.split(SECTION_SPLIT);
    const bullets = [];
    for (const section of sections) {
      const lettered = splitLettered(section);
      for (const item of lettered) {
        if (item.length > 280) {
          bullets.push.apply(bullets, splitSentences(item));
        } else {
          bullets.push(item);
        }
      }
    }

    return bullets.map(clean).filter(Boolean);
  }

  function isGenericRationale(text) {
    const src = clean(text);
    if (!src) return true;
    return GENERIC_RATIONALE.some(function (re) { return re.test(src); });
  }

  function limitSentences(text, max) {
    const sentences = splitSentences(text).filter(Boolean);
    if (!sentences.length) return '';
    return sentences.slice(0, max || 5).join(' ');
  }

  function publishedText(value) {
    const src = clean(value);
    if (!src || isGenericRationale(src)) return '';
    return limitSentences(src, 5);
  }

  function emptyWhy() {
    return { present: '', missing: '', text: '' };
  }

  function formatWhyText(present, missing) {
    return [clean(present), clean(missing)].filter(Boolean).join(' ');
  }

  function whyHasContent(why) {
    if (!why) return false;
    const present = clean(why.present);
    const missing = clean(why.missing);
    const text = clean(why.text);
    if (present && present !== MISSING_RATIONALE) return true;
    if (missing) return true;
    return !!(text && text !== MISSING_RATIONALE);
  }

  function whyFromUnstructured(text) {
    const src = publishedText(text);
    if (!src) return emptyWhy();
    const gapAt = src.search(/\b(?:Held below|Would move)\b/i);
    if (gapAt > 0) {
      return {
        present: limitSentences(src.slice(0, gapAt), 5),
        missing: limitSentences(src.slice(gapAt), 5),
        text: src,
      };
    }
    return { present: src, missing: '', text: src };
  }

  function whyFromValue(value) {
    if (value == null || value === '') return emptyWhy();
    if (typeof value === 'object' && !Array.isArray(value)) {
      const present = clean(value.present || '');
      const missing = clean(value.missing || value.gap || '');
      const text = clean(value.text || '');
      const combined = formatWhyText(present, missing) || text;
      if (!combined || isGenericRationale(combined)) return emptyWhy();
      return {
        present: present ? limitSentences(present, 5) : '',
        missing: missing ? limitSentences(missing, 5) : '',
        text: text || formatWhyText(present, missing),
      };
    }
    return whyFromUnstructured(value);
  }

  const GAP_TO_DIM = [
    ['evidence', /\b(evidence|rct|trial|pubmed|stud(?:y|ies)|replication)\b/i],
    ['formulation', /\b(formula|dose|design|wavelength|parameter|heat system|bioavailab)\b/i],
    ['manufacturing', /\b(manufactur|gmp|\bcoa\b|\bul\b|etl|iso|facilit|certif|third-party)\b/i],
    ['safety', /\b(safety|adverse|contraindicat|recall|side effect)\b/i],
    ['transparency', /\b(transparenc|citation|proprietary blend|undisclosed)\b/i],
    ['sustainability', /\b(sustainab\w*|packaging|sourcing|repair)\b/i],
  ];

  const SENTENCE_TO_DIM = [
    ['evidence', /\b(evidence|rct|trial|pubmed|stud(?:y|ies)|validation|validated|sensitivity|specificity|n=\s*\d|placebo|double-blind|peer-reviewed|510\s*\(\s*k\s*\)|clinical|doi\b|meta-analysis|replication)\b/i],
    ['formulation', /\b(formula|ingredient|dose|design|wavelength|parameter|battery|spec(?:s|ification)?s?|construction|cabin|full-spectrum|rapid-heat|milligram|actives?|disclosed ingredients)\b/i],
    ['manufacturing', /\b(manufactur|gmp|\bcoa\b|\bul\b|etl|iso|facilit|certif|510\s*\(\s*k\s*\)|cleared|warranty|third-party assay|premium-brand construction)\b/i],
    ['safety', /\b(safety|adverse|contraindicat|recall|privacy|data sale|gdpr|side effect|overheat|forbids data)\b/i],
    ['transparency', /\b(transparenc|disclos(?:ed|ure)|citation|published accuracy|named ingredients|claims)\b/i],
    ['sustainability', /\b(sustainab\w*|packaging|sourcing|repair|take-back|e-waste|recycl)\b/i],
  ];

  function mapGapsToDimensions(gapText) {
    const out = emptyRationales();
    const pieces = [];
    splitLettered(gapText).forEach(function (chunk) {
      const gapAt = String(chunk || '').search(/\bWould move\b/i);
      if (gapAt > 0) {
        pieces.push(chunk.slice(0, gapAt), chunk.slice(gapAt));
      } else {
        pieces.push(chunk);
      }
    });
    pieces.forEach(function (chunk) {
      const src = publishedText(chunk);
      if (!src) return;
      const hits = GAP_TO_DIM.filter(function (pair) { return pair[1].test(src); });
      if (hits.length !== 1) return;
      const key = hits[0][0];
      out[key] = out[key] ? out[key] + ' ' + src : src;
    });
    return out;
  }

  function emptyRationales() {
    const out = {};
    DIMENSION_ORDER.forEach(function (key) { out[key] = ''; });
    return out;
  }

  function extractLabeledRationales(explanation) {
    const out = emptyRationales();
    const sentences = splitSentences(explanation);
    let current = null;
    for (const sentence of sentences) {
      const shared = sentence.match(SHARED_MFG_TR_SU);
      if (shared) {
        const body = publishedText(shared[1]);
        if (body) {
          ['manufacturing', 'transparency', 'sustainability'].forEach(function (key) {
            out[key] = out[key] ? out[key] + ' ' + body : body;
          });
        }
        current = null;
        continue;
      }

      let labeled = null;
      let stripped = sentence;
      for (let i = 0; i < LABEL_TO_DIM.length; i++) {
        const key = LABEL_TO_DIM[i][0];
        const re = LABEL_TO_DIM[i][1];
        if (re.test(sentence)) {
          labeled = key;
          stripped = sentence.replace(re, '');
          break;
        }
      }

      if (labeled) {
        current = labeled;
        const body = publishedText(stripped || sentence);
        if (body) out[labeled] = out[labeled] ? out[labeled] + ' ' + body : body;
        continue;
      }

      if (/^(Held below|Would move)\b/i.test(sentence)) {
        current = null;
        continue;
      }

      if (current) {
        const body = publishedText(sentence);
        if (body) out[current] = out[current] ? out[current] + ' ' + body : body;
      }
    }

    DIMENSION_ORDER.forEach(function (key) {
      if (out[key]) out[key] = limitSentences(out[key], 5);
    });
    return out;
  }

  function faqDimension(question) {
    const hits = FAQ_DIMENSION.filter(function (pair) { return pair[1].test(question || ''); });
    return hits.length === 1 ? hits[0][0] : null;
  }

  function extractFaqRationales(faq) {
    const out = emptyRationales();
    if (!Array.isArray(faq)) return out;
    faq.forEach(function (qa) {
      const q = qa && (qa.q || qa.question || '');
      const a = qa && (qa.a || qa.answer || '');
      const dim = faqDimension(q);
      if (!dim || out[dim] || isGenericRationale(a)) return;
      out[dim] = publishedText(a);
    });
    return out;
  }

  function payloadRationales(payload) {
    const out = emptyRationales();
    if (!payload || typeof payload !== 'object') return out;

    const published = payload.dimension_rationales || payload.vsf_dimension_rationales || {};
    DIMENSION_ORDER.forEach(function (key) {
      const direct = payload['vsf_rationale_' + key] || payload['rationale_' + key];
      const fromJson = published[key];
      const fromDim =
        payload.dimensions &&
        payload.dimensions[key] &&
        (payload.dimensions[key].rationale || payload.dimensions[key].why || payload.dimensions[key].note);
      out[key] = fromJson || direct || fromDim || '';
    });
    return out;
  }

  function splitPresentAndGap(explanation) {
    const src = clean(explanation);
    const gapAt = src.search(/\b(?:Held below|Would move|Holds (?:Verified|Elite|Standard|Rejected|Provisional))\b/i);
    if (gapAt >= 0) return { head: src.slice(0, gapAt), gap: src.slice(gapAt) };
    return { head: src, gap: '' };
  }

  function classifySentence(sentence) {
    return SENTENCE_TO_DIM.filter(function (pair) { return pair[1].test(sentence); })
      .map(function (pair) { return pair[0]; });
  }

  function classifyExplanation(explanation) {
    const parts = splitPresentAndGap(explanation);
    const presentByDim = emptyRationales();
    splitSentences(parts.head).forEach(function (sentence) {
      if (isGenericRationale(sentence)) return;
      const body = publishedText(sentence);
      if (!body) return;
      classifySentence(sentence).forEach(function (key) {
        presentByDim[key] = presentByDim[key] ? presentByDim[key] + ' ' + body : body;
      });
    });
    return { presentByDim: presentByDim, gap: parts.gap };
  }

  function dimensionScore(payload, key) {
    const dims = (payload && (payload.dimensions || payload.vsf_dimensions)) || {};
    const raw = dims[key];
    if (raw && typeof raw === 'object') return raw.score != null ? raw.score : raw.value;
    return raw;
  }

  function hasCertifications(payload) {
    const certs = payload && payload.certifications;
    return Array.isArray(certs) && certs.length > 0;
  }

  function recordFallback(payload, key, existing) {
    const label = DIMENSION_LABEL[key];
    const score = dimensionScore(payload, key);
    const presentBits = [];
    const missingBits = [];

    if (existing && existing.present) presentBits.push(existing.present);
    else {
      const scoreBit = score != null && score !== ''
        ? 'The published ' + label + ' score on the record is ' + score + '.'
        : 'A published ' + label + ' score is on the record.';
      presentBits.push(scoreBit);
      presentBits.push('No separate ' + label + ' rationale metafield was stored for this SKU.');
    }

    if (existing && existing.missing) missingBits.push(existing.missing);
    else if (existing && existing.present) {
      missingBits.push('What would raise ' + label + ' is additional published disclosure beyond the note already on the record.');
    } else if (key === 'manufacturing' && !hasCertifications(payload)) {
      missingBits.push('No certifications are stored on the scoring record. A named UL, ETL, CE, GMP, or equivalent mark on the record would strengthen this dimension.');
    } else if (key === 'sustainability') {
      missingBits.push('No repair, sourcing, or packaging disclosure is stored on the scoring record.');
    } else if (key === 'evidence') {
      missingBits.push('No ' + label + '-specific study list is stored beyond the overall published note. A named product-level trial or validation on the record would raise this write-up.');
    } else {
      missingBits.push('A ' + label + '-specific note of what was on the record and what was not was not stored when this SKU was scored.');
    }

    return {
      present: limitSentences(presentBits.join(' '), 5),
      missing: limitSentences(missingBits.join(' '), 5),
    };
  }

  function resolveDimensionRationales(payload) {
    const src = payload && typeof payload === 'object' ? payload : {};
    const published = payloadRationales(src);
    const structured = src.dimension_why || {};
    const labeled = extractLabeledRationales(src.explanation || '');
    const faq = extractFaqRationales(src.faq || []);
    const classified = classifyExplanation(src.explanation || '');
    const gapMap = mapGapsToDimensions(classified.gap);
    const resolved = {};
    DIMENSION_ORDER.forEach(function (key) {
      let why = whyFromValue(structured[key]);
      if (!whyHasContent(why)) why = whyFromValue(published[key]);
      if (!whyHasContent(why)) why = whyFromValue(labeled[key]);
      if (!whyHasContent(why)) why = whyFromValue(faq[key]);
      if (!why.present && classified.presentByDim[key]) {
        why.present = limitSentences(classified.presentByDim[key], 5);
      }
      if (!why.missing && gapMap[key]) {
        why.missing = limitSentences(gapMap[key], 5);
      }
      const filled = recordFallback(src, key, why);
      why = {
        present: filled.present,
        missing: filled.missing,
        text: formatWhyText(filled.present, filled.missing),
      };
      resolved[key] = why;
    });
    return resolved;
  }

  function mount(el) {
    if (!el || typeof document === 'undefined') return;
    const src =
      el.getAttribute('data-vsf-explain-text') ||
      (el.querySelector('.vsf-explain__fallback') || el).textContent;
    const bullets = toBullets(src);
    if (!bullets.length) return;

    const ul = document.createElement('ul');
    ul.className = 'vsf-explain__list';
    bullets.forEach(function (b) {
      const li = document.createElement('li');
      li.textContent = b;
      ul.appendChild(li);
    });
    el.replaceChildren(ul);
  }

  function parsePayload(dialog) {
    const node = dialog && dialog.querySelector('[data-vsf-rubric-payload]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }
  }

  function fillRubricDialog(dialog) {
    if (!dialog || dialog.getAttribute('data-vsf-rubric-filled') === '1') return;
    const payload = parsePayload(dialog);
    if (!payload) return;
    const resolved = resolveDimensionRationales(payload);
    dialog.querySelectorAll('[data-vsf-rubric-rationale]').forEach(function (el) {
      const key = el.getAttribute('data-vsf-rubric-rationale');
      const why = resolved[key];
      if (!why) return;
      const unpublished = !whyHasContent(why);
      el.classList.toggle('vsfrubric__why--missing', unpublished);

      const presentEl = el.querySelector('[data-vsf-rubric-present="' + key + '"]') ||
        dialog.querySelector('[data-vsf-rubric-present="' + key + '"]');
      const missingEl = el.querySelector('[data-vsf-rubric-missing="' + key + '"]') ||
        dialog.querySelector('[data-vsf-rubric-missing="' + key + '"]');

      if (presentEl) {
        presentEl.textContent = why.present || MISSING_RATIONALE;
      } else if (!missingEl) {
        el.textContent = why.present || MISSING_RATIONALE;
      }

      if (missingEl) {
        missingEl.textContent = why.missing || '';
        const part = missingEl.closest('.vsfrubric__part');
        if (part) part.hidden = !why.missing;
      } else if (why.missing) {
        const part = document.createElement('p');
        part.className = 'vsfrubric__part';
        const label = document.createElement('span');
        label.className = 'vsfrubric__part-label';
        label.textContent = 'What was missing';
        const span = document.createElement('span');
        span.setAttribute('data-vsf-rubric-missing', key);
        span.textContent = why.missing;
        part.appendChild(label);
        part.appendChild(document.createTextNode(' '));
        part.appendChild(span);
        el.appendChild(part);
      }
    });
    dialog.setAttribute('data-vsf-rubric-filled', '1');
  }

  function getFocusable(container) {
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return !el.hasAttribute('disabled') && el.offsetParent !== null;
    });
  }

  function fallbackTrap(dialog) {
    const nodes = getFocusable(dialog);
    const first = nodes[0] || dialog;
    const last = nodes[nodes.length - 1] || dialog;
    function onKey(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    dialog.addEventListener('keydown', onKey);
    dialog._vsfRubricTrap = onKey;
    first.focus();
  }

  function releaseFallbackTrap(dialog) {
    if (dialog && dialog._vsfRubricTrap) {
      dialog.removeEventListener('keydown', dialog._vsfRubricTrap);
      dialog._vsfRubricTrap = null;
    }
  }

  function setPageInert(dialog, inert) {
    const body = typeof document !== 'undefined' ? document.body : null;
    if (!body) return;
    body.classList.toggle('vsfrubric-open', !!inert);
  }

  function openRubric(dialog, opener) {
    if (!dialog) return;
    fillRubricDialog(dialog);
    dialog._vsfRubricOpener = opener || document.activeElement;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
      dialog.removeAttribute('hidden');
      fallbackTrap(dialog);
    }
    setPageInert(dialog, true);
    const closeBtn = dialog.querySelector('[data-vsf-rubric-close]');
    if (closeBtn) closeBtn.focus();
    else dialog.focus();
  }

  function closeRubric(dialog) {
    if (!dialog) return;
    const opener = dialog._vsfRubricOpener;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.setAttribute('hidden', '');
      releaseFallbackTrap(dialog);
    }
    setPageInert(dialog, false);
    if (opener && typeof opener.focus === 'function') opener.focus();
    dialog._vsfRubricOpener = null;
  }

  function bindRubricSheet(dialog) {
    if (!dialog || dialog.getAttribute('data-vsf-rubric-bound') === '1') return;
    dialog.setAttribute('data-vsf-rubric-bound', '1');
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog || event.target.hasAttribute('data-vsf-rubric-close')) {
        event.preventDefault();
        closeRubric(dialog);
      }
    });

    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeRubric(dialog);
    });

    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRubric(dialog);
      }
    });
  }

  function bindRubricOpener(el) {
    if (!el || el.getAttribute('data-vsf-rubric-open-bound') === '1') return;
    el.setAttribute('data-vsf-rubric-open-bound', '1');
    el.addEventListener('click', function (event) {
      const targetSel = el.getAttribute('data-vsf-rubric-open');
      const dialog =
        (targetSel && document.querySelector(targetSel)) ||
        document.querySelector('dialog[data-vsf-rubric]');
      if (!dialog) return;
      event.preventDefault();
      openRubric(dialog, el);
    });
  }

  function bindAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('dialog[data-vsf-rubric]').forEach(bindRubricSheet);
    scope.querySelectorAll('[data-vsf-rubric-open]').forEach(bindRubricOpener);
  }

  function mountAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-vsf-explain]').forEach(mount);
    bindAll(scope);
  }

  const api = {
    MISSING_RATIONALE,
    DIMENSION_ORDER,
    DIMENSION_LABEL,
    toBullets,
    splitLettered,
    splitSentences,
    mount,
    mountAll,
    isGenericRationale,
    extractLabeledRationales,
    extractFaqRationales,
    resolveDimensionRationales,
    whyFromValue,
    whyHasContent,
    openRubric,
    closeRubric,
    bindAll,
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { api.mountAll(); });
    } else {
      api.mountAll();
    }
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.VSFExplain = api;
})(typeof window !== 'undefined' ? window : globalThis);
